import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Phone, Menu, X, ArrowRight, Calendar, MapPin } from "lucide-react";
import { Logo } from "@/components/Logo";
import { BUSINESS, SERVICES, CITIES } from "@/siteData";
import { trackEvent } from "@/lib/analytics";

const NAV = [
  { to: "/painting-services", label: "Painting Services", id: "nav-painting-services-link" },
  { to: "/drywall-repair", label: "Drywall Repair", id: "nav-drywall-repair-link" },
  { to: "/service-areas", label: "Service Areas", id: "nav-service-areas-link" },
  { to: "/contact", label: "Contact", id: "nav-contact-link" },
];

export const Layout = ({ children }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="flex min-h-screen flex-col">
      <header className="sticky top-0 z-50 bg-[#0a2a5e] text-white shadow-lg">
        {/* Desktop header */}
        <div className="hidden lg:block">
          <div className="mx-auto flex max-w-7xl items-stretch justify-between px-6 lg:px-12">
            <Link
              to="/"
              data-testid="nav-home-logo"
              aria-label="JC Colors Remodeling LLC home"
              className="flex items-center py-2.5"
            >
              <span className="rounded-lg bg-white p-2 shadow-sm">
                <img
                  src="/logo.png"
                  alt="JC Colors LLC — Drywall & Painting logo"
                  className="h-20 w-auto rounded object-contain"
                />
              </span>
            </Link>
            <nav className="flex items-center gap-8" aria-label="Primary">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={item.id}
                  className={({ isActive }) =>
                    `text-sm font-semibold transition-colors duration-200 hover:text-[#8db7ef] ${
                      isActive ? "text-[#8db7ef]" : "text-white/85"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
            <div className="flex items-stretch">
              <Link
                to="/contact"
                data-testid="header-get-estimate-button"
                className="flex items-center gap-3 border-l border-white/20 px-6 transition-colors duration-200 hover:bg-white/5"
              >
                <Calendar className="h-6 w-6" />
                <span className="text-left text-sm font-semibold leading-tight">
                  Schedule
                  <br />
                  Free Estimate
                </span>
              </Link>
              <a
                href={BUSINESS.phoneHref}
                data-testid="header-call-phone-button"
                onClick={() => trackEvent("phone_click", { location: "header" })}
                className="flex items-center gap-3 border-l border-white/20 px-6 transition-colors duration-200 hover:bg-white/5"
              >
                <Phone className="h-6 w-6" />
                <span className="text-left text-sm font-semibold leading-tight">
                  Click to Call
                  <br />
                  {BUSINESS.phone}
                </span>
              </a>
            </div>
          </div>
        </div>

        {/* Mobile header */}
        <div className="grid grid-cols-[auto_1fr_1fr_auto] items-stretch lg:hidden">
          <Link
            to="/"
            data-testid="mobile-nav-home-logo"
            aria-label="JC Colors Remodeling LLC home"
            className="flex items-center px-3 py-2"
          >
            <span className="rounded-md bg-white p-1.5 shadow-sm">
              <img
                src="/logo.png"
                alt="JC Colors LLC — Drywall & Painting logo"
                className="h-16 w-auto rounded object-contain"
              />
            </span>
          </Link>
          <Link
            to="/contact"
            data-testid="mobile-get-estimate-button"
            className="flex flex-col items-center justify-center gap-1 border-l border-white/20 px-1 py-2"
          >
            <Calendar className="h-5 w-5" />
            <span className="text-center text-[11px] font-semibold leading-tight">
              Schedule
              <br />
              Free Estimate
            </span>
          </Link>
          <a
            href={BUSINESS.phoneHref}
            data-testid="mobile-call-phone-button"
            onClick={() => trackEvent("phone_click", { location: "mobile-header" })}
            className="flex flex-col items-center justify-center gap-1 border-l border-white/20 px-1 py-2"
          >
            <Phone className="h-5 w-5" />
            <span className="text-[11px] font-semibold">Click to Call</span>
          </a>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            aria-label={open ? "Close menu" : "Open menu"}
            className="flex flex-col items-center justify-center gap-1 border-l border-white/20 px-4"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            <span className="text-[11px] font-semibold">Menu</span>
          </button>
        </div>

        {/* Location strip */}
        <div className="border-t border-white/10 bg-[#071d42]">
          <div className="mx-auto flex max-w-7xl items-center gap-2 px-4 py-2 text-xs font-medium text-white/80 sm:px-6 lg:px-12">
            <MapPin className="h-3.5 w-3.5 shrink-0 text-[#8db7ef]" />
            <span data-testid="header-location-strip">
              JC Colors Painting &amp; Drywall of Overland Park
              <span className="hidden sm:inline"> — Serving the Kansas City Metro</span>
            </span>
          </div>
        </div>

        {/* Mobile menu panel */}
        {open && (
          <nav
            className="border-t border-white/10 bg-[#0a2a5e] px-4 py-4 lg:hidden"
            aria-label="Mobile"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-1">
              {NAV.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={`mobile-${item.id}`}
                  onClick={() => setOpen(false)}
                  className="rounded-lg px-3 py-2.5 text-base font-semibold text-white/90 hover:bg-white/10"
                >
                  {item.label}
                </Link>
              ))}
              <Link
                to="/interior-painting"
                data-testid="mobile-nav-interior-painting-link"
                onClick={() => setOpen(false)}
                className="rounded-lg px-3 py-2.5 text-base font-semibold text-white/90 hover:bg-white/10"
              >
                Interior Painting
              </Link>
              <Link
                to="/exterior-painting"
                data-testid="mobile-nav-exterior-painting-link"
                onClick={() => setOpen(false)}
                className="rounded-lg px-3 py-2.5 text-base font-semibold text-white/90 hover:bg-white/10"
              >
                Exterior Painting
              </Link>
              <a
                href={BUSINESS.phoneHref}
                data-testid="mobile-menu-call-button"
                onClick={() => trackEvent("phone_click", { location: "mobile-menu" })}
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-[#1e6ff2] px-5 py-3 text-sm font-semibold text-white"
              >
                <Phone className="h-4 w-4" />
                Call {BUSINESS.phone}
              </a>
            </div>
          </nav>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="bg-slate-900 text-slate-300">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-12">
          <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-4">
            <div>
              <Link to="/" data-testid="footer-home-logo" aria-label="JC Colors Remodeling LLC home">
                <Logo dark />
              </Link>
              <p className="mt-5 text-sm leading-relaxed text-slate-400">
                Residential painting and drywall services across the Kansas City
                metro — careful prep, clean lines and finishes built to last.
              </p>
            </div>
            <div>
              <h3 className="font-display text-sm font-bold uppercase tracking-[0.2em] text-amber-500">
                Services
              </h3>
              <ul className="mt-4 space-y-2.5 text-sm">
                <li>
                  <Link to="/painting-services" data-testid="footer-painting-services-link" className="transition-colors hover:text-amber-400">
                    Painting Services
                  </Link>
                </li>
                {SERVICES.map((s) => (
                  <li key={s.slug}>
                    <Link to={`/${s.slug}`} data-testid={`footer-${s.slug}-link`} className="transition-colors hover:text-amber-400">
                      {s.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="font-display text-sm font-bold uppercase tracking-[0.2em] text-amber-500">
                Service Areas
              </h3>
              <ul className="mt-4 space-y-2.5 text-sm">
                <li>
                  <Link to="/service-areas" data-testid="footer-service-areas-link" className="transition-colors hover:text-amber-400">
                    All Service Areas
                  </Link>
                </li>
                {CITIES.map((c) => (
                  <li key={c.slug}>
                    <Link
                      to={`/service-areas/${c.slug}`}
                      data-testid={`footer-${c.slug}-link`}
                      className="transition-colors hover:text-amber-400"
                    >
                      {c.city}, {c.state}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="font-display text-sm font-bold uppercase tracking-[0.2em] text-amber-500">
                Contact
              </h3>
              <ul className="mt-4 space-y-2.5 text-sm">
                <li>
                  <a
                    href={BUSINESS.phoneHref}
                    data-testid="footer-call-phone-link"
                    onClick={() => trackEvent("phone_click", { location: "footer" })}
                    className="font-semibold text-white transition-colors hover:text-amber-400"
                  >
                    {BUSINESS.phone}
                  </a>
                </li>
                <li>
                  <a href={BUSINESS.emailHref} data-testid="footer-email-link" className="transition-colors hover:text-amber-400">
                    {BUSINESS.email}
                  </a>
                </li>
                <li className="text-slate-400">
                  Serving Overland Park KS, Kansas City MO, Lenexa KS &amp; Olathe KS
                </li>
                <li>
                  <Link
                    to="/contact"
                    data-testid="footer-get-estimate-button"
                    className="mt-2 inline-flex items-center gap-2 rounded-full bg-amber-600 px-5 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:scale-105 hover:bg-amber-700"
                  >
                    Get a Free Estimate
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <div className="mt-12 border-t border-slate-800 pt-6 text-xs text-slate-500">
            © {new Date().getFullYear()} {BUSINESS.name}. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};
